import {
  CANVAS_ACTION_CATALOG,
  type CanvasActionName,
  type CanvasEditPlanFormatReceipt,
  type CanvasFormatWriteReceipt,
  type CanvasHeaderFooterOtherPart,
  type CanvasHeaderFooterPageNumbers,
  type CanvasImageReceipt,
  type CanvasPageLayoutReceipt,
  type CanvasRewriteReceipt,
  type CanvasHeaderFooterPartsAfter,
  type CanvasHeaderFooterType,
  type CanvasReplaceEverywhereReceipt,
} from '@hector/shared-types';
import { REFUS_TEXTE_DANS_UN_CHAMP } from './champsVides';

export interface CanvasSnapshotSummary {
  revision: string;
  counts: { blocks: number; trackedChanges: number; comments: number };
}

export interface ResolvedActionTarget {
  nodeId: string;
  ordinal: number;
  text: string;
  candidateCount?: number;
}

export interface VerificationCheck {
  name: string;
  passed: boolean;
  message?: string;
}

/**
 * Les CINQ issues d'une action (#939, #990). Chacune autorise une conduite
 * différente, et c'est tout leur intérêt : les fondre rend l'agent aveugle.
 *
 * - `ok` — l'effet demandé est prouvé sur le document.
 * - `partial` — une partie de ce qui était demandé a été appliquée, l'autre
 *   non. Inclut l'écriture obtenue **sans le régime demandé**
 *   (`TRACKING_NOT_APPLIED`) : le texte est là, la révision suivie non.
 *   ⚠️ L'effet partiel est ACQUIS — rejouer duplique.
 * - `failed` — l'effet demandé n'a pas eu lieu ; réinspecter puis retenter est
 *   la bonne conduite.
 * - `aborted` — l'action a été **refusée avant toute mutation** : le document
 *   est intact. Sortir par un autre verbe ou un autre réglage, jamais par une
 *   relance à l'identique.
 * - `non_verifiable` — le document a bougé pour une autre raison pendant
 *   l'action : son effet n'est **ni prouvé ni infirmé**. ⚠️ Cette issue ne
 *   déclenche **aucune** nouvelle tentative — retenter à l'aveugle sur un
 *   document qui a changé duplique l'édition.
 *
 * Sans `non_verifiable`, une frappe de l'avocat concomitante devient
 * mécaniquement un « échec de vérification » suivi d'une relance : c'est la
 * boucle de refus reconstituée par un autre chemin. Un signal absent n'est pas
 * un signal négatif. Sans `aborted`, un refus préalable se lit comme un échec
 * d'écriture, et l'agent réinspecte un document que personne n'a touché.
 */
export type CanvasActionStatus = 'ok' | 'partial' | 'failed' | 'aborted' | 'non_verifiable';

export interface CanvasActionReceipt {
  intent: CanvasActionName;
  status: CanvasActionStatus;
  reviewability: 'tracked' | 'direct_untracked' | 'mixed' | 'none';
  preSnapshot: CanvasSnapshotSummary;
  postSnapshot: CanvasSnapshotSummary;
  resolvedTargets: ResolvedActionTarget[];
  effects: Array<{ kind: string; reviewability: 'tracked' | 'direct_untracked' | 'mixed' | 'none'; description: string; nodeIds?: string[] }>;
  verification: VerificationCheck[];
  verificationPassed: boolean;
  errors: Array<{ code: string; message: string; stepId?: string; nextAction?: string }>;
  formattingMatched?: boolean;
  /**
   * ⚠️ `'retry'` n'est admis que sur un receipt `aborted` (#991) — jamais après
   * une mutation. Le contrat partagé l'autorise depuis toujours ; ce type le
   * refusait, parce que rejouer une action partiellement appliquée duplique du
   * contenu dans l'acte de l'avocat. Un refus AVANT mutation n'a rien écrit :
   * l'objection ne porte pas, et l'interdire forçait à ranger « réessaie sans
   * source de style » sous `reinspect`, c'est-à-dire à ordonner en machine le
   * contraire de ce que le message dit en clair.
   */
  recovery?: { action: 'reinspect' | 'stop' | 'retry'; reason: string };
  /**
   * Ce que la RELECTURE avant/après a montré des révisions suivies.
   *
   * ⚠️ Ces identités ne viennent **jamais** du receipt d'insertion : vérifié
   * dans les `.d.ts` publics de 1.44.1, celui-ci porte le succès, le code
   * d'échec, la plage touchée et une révision opaque — mais ni les nœuds créés,
   * ni les identifiants de révision. Les chercher là-bas reviendrait à chercher
   * une donnée qui n'existe pas.
   */
  revisionIdentities?: {
    nouvelles: string[];
    disparues: string[];
    /** Disparitions que l'action n'a PAS demandées — la trace d'un autre auteur. */
    disparuesInattendues: string[];
  };
  /** Résultat atomique, par étape stable, d'un `apply_edit_plan` (#1610). */
  editPlan?: {
    revision: { before: string; after: string };
    steps: Array<{
      id: string;
      op: 'assert_text' | 'text_rewrite' | 'text_insert' | 'text_delete' | 'format_apply';
      effect: 'changed' | 'noop' | 'error' | 'assert_passed' | 'assert_failed';
      matchCount: number;
      trackedChangeIds?: string[];
      verified: boolean;
      message?: string;
    }>;
    /** La forme posée par les étapes `format_apply`, relue au fichier exporté après le plan (`formeDuPlan.ts`, #1947). */
    format?: CanvasEditPlanFormatReceipt;
  };
  /**
   * Les faits propres à `set_header_footer` — voir `headerFooter` au contrat
   * partagé, qui porte la justification de chaque champ.
   *
   * Rempli par le seul `headerFooterActionExecutors`, et transporté tel quel par
   * `buildVerifiedReceipt` : les autres verbes n'ont rien à y écrire.
   */
  headerFooter?: CanvasHeaderFooterReceipt;
  /**
   * Ce qu'une écriture de forme a fait, relu dans le fichier exporté après
   * l'écriture (#1863). Rempli par les exécuteurs de forme (`formatTextExecutor`, `formatParagraphsExecutor`).
   */
  format?: CanvasFormatWriteReceipt;
  /** Ce que `set_page_layout` a fait, relu dans le fichier exporté (`pageLayoutExecutor.ts`). */
  pageLayout?: CanvasPageLayoutReceipt;
  /**
   * Ce qu'un `replace_text` en mode partout a trouvé et écrit (`replaceEverywhereExecutor.ts`) : sur un refus, les
   * couples à trancher et leurs emplacements. Rempli par ce seul exécuteur ; `abortedBeforeMutation` le transporte tel quel.
   */
  replaceEverywhere?: CanvasReplaceEverywhereReceipt;
  /** Ce que `rewrite_block` a fait, relu dans le fichier exporté (`rewriteBlockExecutor.ts`). */
  rewrite?: CanvasRewriteReceipt;
  /** Ce qu'`edit_image` a fait, d'après la liste des images relue après l'écriture (`imageActionExecutors.ts`). */
  image?: CanvasImageReceipt;
}

/** Ce que `set_header_footer` publie de sa relecture — miroir du contrat partagé. */
export interface CanvasHeaderFooterReceipt {
  target: 'header' | 'footer';
  type: CanvasHeaderFooterType;
  section_index: number;
  mode: 'replace' | 'append';
  paragraphs_written: number;
  previous_text: string;
  previous_had_image: boolean;
  first_page_flag: 'raised' | 'already_on' | 'not_applicable';
  warnings: string[];
  parts_after: CanvasHeaderFooterPartsAfter;
  other_part: CanvasHeaderFooterOtherPart;
  /** Les numéros de page de la zone relus au fichier, quand l'appel en posait ou en retirait. */
  page_numbers?: CanvasHeaderFooterPageNumbers;
  /** Les écritures acceptées par l'éditeur, une entrée d'historique chacune : le compte qu'`undo_changes` défait. */
  writes: number;
}

/**
 * Réduit un instantané de canvas au RÉSUMÉ que le contrat de receipt accepte.
 *
 * ⚠️ **LES TROIS CLÉS SE RECOPIENT UNE À UNE, ON NE PASSE JAMAIS `counts` PAR RÉFÉRENCE.**
 * `CanvasSnapshotSummarySchema.counts` est `.strict()` côté backend et n'admet que
 * `blocks`, `trackedChanges` et `comments` — or l'instantané réel en porte une quatrième,
 * `blocksTotalExact` (`canvasSnapshot.ts:762`), qui dit si le compte de blocs est exact ou
 * seulement un minorant. Renvoyer `snapshot.counts` tel quel la faisait voyager jusqu'au
 * backend, où `CanvasActionReceiptSchema.parse()` levait `unrecognized_keys`.
 *
 * TypeScript ne pouvait pas l'attraper : le contrôle des propriétés excédentaires ne
 * s'applique qu'aux littéraux d'objet frais, jamais à une variable passée telle quelle.
 * Le type du paramètre annonçait donc trois clés pendant que quatre transitaient.
 *
 * CE QUE ÇA CASSAIT, mesuré sur staging le 2026-09-10. Le `parse` levait pour CHAQUE
 * action canvas, dans un `catch` qui ne disait rien. Trois conséquences, toutes muettes :
 * l'inspection était invalidée après chaque mutation, forçant un `canvas_inspect` avant
 * la suivante et faisant refuser celles qui s'enchaînaient ; le markdown n'était jamais
 * retenu par le backend, si bien que `save_plan_structure` enregistrait le résumé du
 * modèle — six `write_markdown` posés, 5 077 caractères sauvegardés ; et aucune erreur
 * n'atteignait ni l'avocat ni le log.
 *
 * Si une clé s'ajoute un jour à `counts`, elle ne doit PAS arriver ici par surprise :
 * c'est le contrat backend qui décide de ce qui voyage.
 */
export function toCanvasSnapshotSummary(snapshot: {
  revision: string;
  counts: { blocks: number; trackedChanges: number; comments: number };
}): CanvasSnapshotSummary {
  const { blocks, trackedChanges, comments } = snapshot.counts;
  return { revision: snapshot.revision, counts: { blocks, trackedChanges, comments } };
}

interface PreuveDIdentite {
  /** Identités de révision relues AVANT l'action. */
  revisionsAvant?: readonly string[];
  /** Identités de révision relues APRÈS l'action, du document lui-même. */
  revisionsApres?: readonly string[];
  /** Identités que l'action demandait explicitement de faire disparaître (accept/reject). */
  identitesAttendues?: { disparues: readonly string[] };
  /**
   * Quel MOUVEMENT d'identités prouve l'effet demandé.
   *
   * `apparition` — une révision suivie doit être née (réécriture, remplacement,
   * insertion). Une disparition non demandée y signale alors un autre auteur.
   *
   * `apparition-ou-disparition` — une **suppression** suivie produit l'un OU
   * l'autre, et c'est mesuré, pas supposé : supprimer du contenu accepté ajoute
   * une marque de suppression, tandis que supprimer une insertion suivie encore
   * en attente l'ANNULE (observé en 1.44). Exiger une apparition ferait échouer
   * la moitié des suppressions légitimes — c'est ce qu'a montré la sonde qui
   * monte le vrai SuperDoc.
   *
   * ⚠️ Limite assumée sur ce second mode : `trackChanges.list()` ne rattache pas
   * une identité à un nœud, donc une annulation par l'action est
   * indistinguable d'une annulation par une frappe de l'avocat. La troisième
   * issue ne peut donc pas se déclencher sur une suppression — la postcondition
   * de nœud (`blocks_deleted_or_marked`) y reste la preuve d'effet.
   */
  /**
   * `aucun` — le geste **ne produit pas** de révision suivie, et c'est MESURÉ,
   * pas supposé (une insertion sous `superdoc@2` : le mode suivi est accepté et
   * aucune identité n'apparaît, #980).
   *
   * Il n'y a alors aucune postcondition d'identité à exiger — la preuve d'effet
   * vient des postconditions de nœud et du mouvement de révision du document.
   * ⚠️ Mais les identités restent RELUES et comparées : la troisième issue de
   * #939 (c) — « une révision a disparu sans qu'on l'ait demandé, donc un autre
   * auteur a touché le document » — doit continuer de s'exercer. C'est
   * précisément ce qu'on perdrait en cessant de publier `revisionsAvant/Apres`
   * sur ce chemin.
   */
  mouvementAttendu?: 'apparition' | 'apparition-ou-disparition' | 'aucun';
  /**
   * Les révisions DÉJÀ ouvertes dans lesquelles l'écriture a pu se fondre, établies par l'exécuteur sur un fait
   * relu — jamais sur le mode demandé.
   *
   * WHY : une retouche faite dans un texte encore en insertion suivie ne crée aucune identité, l'éditeur la range dans
   * la révision en attente. Le texte a changé et l'avocat le voit toujours comme une révision ; « écrit sans suivi »
   * le lui fait dire faux (conversation de production du 2026-10-13). Elles ne valent preuve que si CHACUNE est
   * ouverte avant ET après l'écriture, et seulement quand aucune identité neuve n'est apparue.
   */
  revisionsAbsorbantes?: readonly string[];
  /** Repli pour les actions qui ne posent aucune révision suivie (`undo`). */
  revisionAvant?: string;
  revisionApres?: string;
}

interface ResultatDIdentite {
  prouve: boolean;
  documentABouge: boolean;
  check: VerificationCheck | null;
  identities?: CanvasActionReceipt['revisionIdentities'];
  /** Vrai quand l'écriture est prouvée suivie par des {@link PreuveDIdentite.revisionsAbsorbantes}, sans identité neuve. */
  absorbee?: true;
}

/**
 * Ancre la preuve d'effet sur des **identités**, jamais sur des compteurs.
 *
 * Un compteur global (`postSnapshot.counts.trackedChanges > pre`) ment dans les
 * deux sens : une frappe de l'avocat qui annule une insertion suivie en attente
 * le fait **baisser** (mesuré en 1.44), et une révision posée par quelqu'un
 * d'autre le fait monter sans que l'action ait rien fait. Les identités
 * présentes après et absentes avant, elles, sont attribuables.
 */
function evaluerIdentites(preuve: PreuveDIdentite): ResultatDIdentite {
  if (preuve.revisionsAvant === undefined || preuve.revisionsApres === undefined) {
    // Action sans révision suivie (undo) : la révision du document fait foi.
    if (preuve.revisionAvant === undefined || preuve.revisionApres === undefined) {
      return { prouve: true, documentABouge: false, check: null };
    }
    const bouge = preuve.revisionApres !== preuve.revisionAvant;
    return {
      prouve: bouge,
      documentABouge: false,
      check: { name: 'document_revision_moved', passed: bouge, message: 'The document revision read before and after the action must differ.' },
    };
  }

  const avant = new Set(preuve.revisionsAvant);
  const apres = new Set(preuve.revisionsApres);
  const nouvelles = [...apres].filter((id) => !avant.has(id));
  const disparues = [...avant].filter((id) => !apres.has(id));
  const demandees = new Set(preuve.identitesAttendues?.disparues ?? []);
  const disparuesInattendues = disparues.filter((id) => !demandees.has(id));

  const identities = { nouvelles, disparues, disparuesInattendues };

  if (demandees.size > 0) {
    const toutesDisparues = [...demandees].every((id) => !apres.has(id));
    return {
      prouve: toutesDisparues,
      // Une disparition NON demandée pendant une décision explicite signale un
      // autre auteur sur le même document.
      documentABouge: disparuesInattendues.length > 0,
      check: {
        name: 'requested_revision_ids_gone',
        passed: toutesDisparues,
        message: 'Every explicitly requested tracked-change id must be absent from the post-action re-read.',
      },
      identities,
    };
  }

  if (preuve.mouvementAttendu === 'aucun') {
    return {
      prouve: true,
      // La garde (c) de #939 s'exerce ici, sauf l'exception étroite d'une
      // réécriture directe de corps : ses révisions corps déjà inventoriées
      // disparaissent avec le contenu sans devenir des acceptations/rejets.
      // Toute autre disparition reste la signature d'un autre auteur.
      documentABouge: disparuesInattendues.length > 0,
      check: null,
      identities,
    };
  }

  if (preuve.mouvementAttendu === 'apparition-ou-disparition') {
    const aBouge = nouvelles.length > 0 || disparues.length > 0;
    return {
      prouve: aBouge,
      // Pas de troisième issue ici : une disparition est un effet LÉGITIME de
      // l'action, donc elle ne peut pas servir de signature d'un autre auteur.
      documentABouge: false,
      check: {
        name: 'revision_identities_moved',
        passed: aBouge,
        message: 'A tracked-change identity must have appeared or disappeared between the pre- and post-action re-reads (a tracked delete either adds a deletion mark or cancels a pending insertion).',
      },
      identities,
    };
  }

  const absorbantes = preuve.revisionsAbsorbantes ?? [];
  if (nouvelles.length === 0 && absorbantes.length > 0 && absorbantes.every((id) => avant.has(id) && apres.has(id))) {
    return {
      prouve: true,
      documentABouge: disparuesInattendues.length > 0,
      check: {
        name: 'edit_absorbed_by_open_revision',
        passed: true,
        message: `No new tracked-change identity: the edit lives inside tracked change(s) already open on its target before the action and still open after it (${absorbantes.slice(0, 8).join(', ')}).`,
      },
      identities,
      absorbee: true,
    };
  }

  return {
    prouve: nouvelles.length > 0,
    // Une révision suivie qui DISPARAÎT alors qu'on en posait une, c'est la
    // signature d'une frappe de l'avocat qui annule une insertion en attente.
    documentABouge: disparuesInattendues.length > 0,
    check: {
      name: 'new_revision_identities',
      passed: nouvelles.length > 0,
      message: 'At least one tracked-change identity must be present in the post-action re-read and absent from the pre-action one.',
    },
    identities,
  };
}

/** A successful provider reply is evidence of transport, never proof of the requested document effect. */
/**
 * Ce que l'avocat pourra RELIRE de cette action — établi par la RELECTURE, et
 * jamais par le mode demandé (#990).
 *
 * ⚠️ La valeur s'est d'abord écrite `'tracked'` en dur, puis s'est déduite du
 * régime demandé. Les deux mentaient, pour la même raison : demander le suivi
 * ne l'obtient pas. Mesuré le 2026-08-16 sur `superdoc@2.6.0`, `insert`
 * s'annonce `tracked:true`, est accepté en `changeMode:'tracked'` et ne produit
 * **aucune** révision, pendant que `replace` et `blocks.delete` en produisent
 * une. Un receipt qui annonce « tracked » sur cette base conduit l'agent à dire
 * à l'avocat d'aller accepter dans Word des révisions qui n'existent pas —
 * c'est la moitié du défaut vécu.
 *
 * Le juge est donc le compte d'identités de révision NEUVES, relu après coup.
 *
 * Quatre cas où la relecture ne tranche pas, et ce qu'on en dit :
 * - `undo_changes` ne pose pas de révision, il en retire → `none`.
 * - `set_review_mode` règle le suivi et n'écrit rien dans l'acte → `none`.
 * - accepter/refuser des révisions en RETIRE aussi : rien de neuf à relire →
 *   `none`.
 * - le provider a refusé, ou rien n'a été relu : rien n'a été écrit dont
 *   l'avocat puisse disposer → `none`. Ne jamais rendre `direct_untracked`
 *   ici, qui affirmerait qu'un texte est dans le document.
 */
function reviewabiliteRelue(input: {
  intent: CanvasActionName;
  estDecision: boolean;
  ecritureAboutie: boolean;
  identitesNeuves: number | undefined;
  /** L'écriture s'est fondue dans une révision déjà ouverte, relue avant et après : l'avocat la voit en révision. */
  absorbee: boolean;
}): CanvasActionReceipt['reviewability'] {
  // `set_review_mode` règle le suivi sans rien écrire dans l'acte : le rendre
  // `direct_untracked` affirmerait qu'un texte a été posé.
  if (input.intent === 'undo_changes' || input.intent === 'set_review_mode' || input.estDecision) return 'none';
  if (!input.ecritureAboutie || input.identitesNeuves === undefined) return 'none';
  return input.identitesNeuves > 0 || input.absorbee ? 'tracked' : 'direct_untracked';
}

export function buildVerifiedReceipt(input: {
  intent: CanvasActionName;
  preSnapshot: CanvasSnapshotSummary;
  postSnapshot: CanvasSnapshotSummary;
  resolvedTargets: ResolvedActionTarget[];
  provider: unknown;
  verification: VerificationCheck[];
  effects?: CanvasActionReceipt['effects'];
  /**
   * Régime de révision du document au moment de l'action (#990). `undefined`
   * vaut « suivi » : sans régime connu, le reçu exige la preuve la plus forte.
   * `superDocCanvasExecutor` passe toujours le régime réel du document.
   */
  regimeSuivi?: boolean;
  /** Les faits de relecture propres à `set_header_footer` — transportés tels quels. */
  headerFooter?: CanvasHeaderFooterReceipt;
  /** Les faits de relecture propres à `edit_image` — transportés tels quels. */
  image?: CanvasImageReceipt;
} & PreuveDIdentite): CanvasActionReceipt {
  const providerSucceeded = !isProviderFailure(input.provider);
  // ⚠️ En régime DIRECT, aucune révision suivie ne peut apparaître — exiger une
  // identité neuve ferait échouer TOUTE édition réussie que l'avocat a
  // délibérément demandée en direct. Le défaut existait depuis que le régime est
  // devenu réglable (#990, première moitié) : le receipt rendait `failed` sur un
  // texte pourtant écrit. Les identités restent RELUES — la troisième issue de
  // #939 (une révision disparue sans qu'on l'ait demandé) doit continuer de
  // s'exercer, c'est précisément ce que `'aucun'` préserve.
  const identite = evaluerIdentites(
    input.regimeSuivi === false ? { ...input, mouvementAttendu: 'aucun' } : input,
  );
  const verification = identite.check === null ? input.verification : [...input.verification, identite.check];
  const verificationPassed = providerSucceeded && verification.length > 0 && verification.every((check) => check.passed);

  const estDecision = (input.identitesAttendues?.disparues.length ?? 0) > 0;
  // Les postconditions MÉTIER, sans la garde d'identité : ce sont elles qui
  // disent si le texte demandé est dans le document. Les séparer est ce qui
  // permet de distinguer « rien n'a été écrit » de « écrit, mais sans suivi ».
  const postconditionsMetierTenues = input.verification.length > 0 && input.verification.every((check) => check.passed);
  const ecritureAboutie = providerSucceeded && postconditionsMetierTenues;

  /**
   * Le régime suivi a été demandé, l'écriture a eu lieu, et la relecture ne
   * montre AUCUN mouvement de révision (#990).
   *
   * `reviewedOperations` non vide écarte les verbes qui ne produisent
   * structurellement aucune révision — accepter/refuser, annuler : leur
   * reprocher l'absence de révision serait leur reprocher d'exister.
   * Une DISPARITION suffit à écarter le cas : une suppression suivie qui annule
   * une insertion en attente est un effet suivi légitime, pas une écriture nue.
   */
  const suiviDemandeNonObtenu = ecritureAboutie
    && input.regimeSuivi !== false
    && CANVAS_ACTION_CATALOG[input.intent].reviewedOperations.length > 0
    && !estDecision
    && identite.identities !== undefined
    && identite.absorbee !== true
    && identite.identities.nouvelles.length === 0
    && identite.identities.disparues.length === 0;

  const status: CanvasActionStatus = suiviDemandeNonObtenu
    ? 'partial'
    : verificationPassed
      ? 'ok'
      // La troisième issue ne s'applique QUE quand le provider a répondu : un refus
      // du provider est un échec net, pas une incertitude.
      : providerSucceeded && identite.documentABouge
        ? 'non_verifiable'
        : 'failed';

  const errors = status === 'ok'
    ? []
    : [{
        code: status === 'partial'
          ? 'TRACKING_NOT_APPLIED'
          : status === 'non_verifiable'
            ? 'VERIFICATION_INCONCLUSIVE'
            : providerSucceeded ? 'VERIFICATION_FAILED' : 'PROVIDER_FAILURE',
        message: status === 'partial'
          // ⚠️ Ce contrôle est le SEUL contrôle vrai du receipt sur la
          // relisibilité, et il était noyé dans un `failed` générique — d'où un
          // agent qui le traitait comme du bruit et désinformait l'avocat. Il
          // porte donc son propre code, et son message dit ce qui est VRAI du
          // texte plutôt que ce qui a échoué.
          ? `canvas_perform_action ${input.intent} : le texte demandé EST dans le document, mais SANS révision suivie. `
            + 'L\'éditeur a accepté le mode suivi et n\'a produit aucune modification suivie (mesuré sur ce build). '
            + 'Ne dis PAS à l\'avocat d\'aller accepter ou refuser ces modifications : il n\'en verra aucune dans Word. '
            + 'Ne rejoue pas l\'action — l\'écriture est acquise, la relancer la dupliquerait. '
            + 'Dis ce qui a été écrit et où, et signale que ce passage n\'est pas révisable.'
          : status === 'non_verifiable'
            ? `canvas_perform_action ${input.intent} could NOT be verified: the document changed for another reason while the action ran `
              + `(tracked-change ids disappeared without being asked to: ${identite.identities?.disparuesInattendues.join(', ') ?? 'none'}). `
              + 'The edit may or may not have been applied. Do NOT retry: run canvas_inspect and decide from the actual document state.'
            : providerSucceeded
              ? `canvas_perform_action ${input.intent} did not satisfy every mandatory postcondition after the provider reported success. ${decrireLesVerificationsEnEchec(verification)} Stop and inspect the affected target before another mutation.`
              : `canvas_perform_action ${input.intent} was rejected by the document provider${raisonDuFournisseur(input.provider, input.intent)}`,
      }];

  const reviewability = reviewabiliteRelue({
    intent: input.intent,
    estDecision,
    ecritureAboutie,
    identitesNeuves: identite.identities?.nouvelles.length,
    absorbee: identite.absorbee === true,
  });

  return {
    intent: input.intent,
    status,
    reviewability,
    preSnapshot: input.preSnapshot,
    postSnapshot: input.postSnapshot,
    resolvedTargets: input.resolvedTargets,
    // ⚠️ Un effet ne peut pas se déclarer plus relisible que la relecture ne
    // l'établit (#990). Les exécuteurs écrivaient `'tracked'` en dur dans leurs
    // effets ; le receipt disait alors la vérité dans son champ de tête et la
    // contredisait trois lignes plus bas — et c'est la ligne d'effet, qui nomme
    // le geste, que le modèle cite à l'avocat. Le rabattement ne va que dans le
    // sens de la prudence : un effet qui se déclare DÉJÀ direct ou nul n'est
    // jamais promu.
    effects: (input.effects ?? []).map((effet) => (
      effet.reviewability === 'tracked' && reviewability !== 'tracked'
        ? { ...effet, reviewability }
        : effet
    )),
    verification,
    verificationPassed,
    errors,
    // Transporté tel quel, jamais recalculé : ce sont des faits RELUS du
    // document par l'exécuteur d'en-tête, et le receipt n'a rien à en déduire.
    ...(input.headerFooter === undefined ? {} : { headerFooter: input.headerFooter }),
    ...(input.image === undefined ? {} : { image: input.image }),
    ...(identite.identities === undefined ? {} : { revisionIdentities: identite.identities }),
    ...(status === 'ok'
      ? {}
      : {
          recovery: status === 'non_verifiable'
            // `stop`, jamais `reinspect` : « réinspecte » se lit comme une
            // invitation à retenter, et retenter une édition dont on ignore si
            // elle a eu lieu la duplique.
            ? { action: 'stop' as const, reason: 'The document moved for another reason during the action; the effect is neither proven nor disproven.' }
            : status === 'partial'
              // Même raison, sur un fait plus net : l'écriture a ABOUTI, seul le
              // suivi manque. Rejouer dupliquerait un texte déjà en place.
              ? { action: 'stop' as const, reason: 'The requested text was written but produced no tracked revision; the edit is already applied and must not be replayed.' }
              : { action: 'reinspect' as const, reason: 'The provider result was not independently verified.' },
        }),
  };
}

/** Nombre de vérifications en échec nommées dans un message, et longueur de ce que chacune y dit : un reçu reste borné. */
const VERIFICATIONS_EN_ECHEC_NOMMEES_MAX = 4;
const LONGUEUR_MAX_DU_CONSTAT = 300;

/**
 * Nomme les vérifications qui ont échoué, avec ce que chacune a constaté.
 *
 * WHY : « did not satisfy every mandatory postcondition » ne disait pas laquelle, donc ni ce qui restait à faire ni
 * ce qui était déjà fait (conversation de production du 2026-10-13, sur une suppression de tableau).
 */
function decrireLesVerificationsEnEchec(verification: readonly VerificationCheck[]): string {
  const enEchec = verification.filter((check) => !check.passed);
  if (enEchec.length === 0) return 'No named check failed: the receipt carries no check that proves the effect.';
  const nommees = enEchec.slice(0, VERIFICATIONS_EN_ECHEC_NOMMEES_MAX).map((check) => {
    const constat = check.message === undefined ? 'no detail' : check.message.length > LONGUEUR_MAX_DU_CONSTAT ? `${check.message.slice(0, LONGUEUR_MAX_DU_CONSTAT)}…` : check.message;
    return `${check.name} — ${constat}`;
  });
  const reste = enEchec.length - nommees.length;
  return `Failed check(s): ${nommees.join(' | ')}${reste > 0 ? ` | and ${reste} more in verification[]` : ''}.`;
}

/**
 * Refus préalables dont le MESSAGE suffit à corriger l'appel : relire rendrait
 * le même document, et le broker garde la portée d'un `aborted` à révision
 * inchangée (`CanvasSessionBroker.observeActionReceipt`). Un sélecteur ambigu
 * nomme ses candidats par `nodeId` ; les ordonner « reinspect » coûtait une
 * relecture complète pour une information déjà rendue (audit 2026-10-01).
 */
const REFUS_A_CORRIGER_SANS_RELIRE: ReadonlySet<string> = new Set(['AMBIGUOUS_SELECTOR']);

/**
 * L'action a été refusée **avant** toute mutation : le document est intact.
 *
 * ⚠️ `aborted`, jamais `failed` (#990) : les deux se lisaient pareil, et c'est
 * la différence qui compte pour la suite. « Échoué » laisse planer un doute sur
 * ce qui a bougé et invite à réinspecter ; ici rien n'a bougé, et la sortie est
 * de changer de verbe, de cible ou de réglage.
 *
 * `reviewability: 'none'` pour la même raison : rien n'a été écrit, il n'y a
 * donc rien à relire — l'annoncer `tracked` ferait chercher à l'avocat des
 * révisions qui n'existent pas.
 */
export function abortedBeforeMutation(input: {
  intent: CanvasActionName;
  snapshot: CanvasSnapshotSummary;
  code: string;
  message: string;
  /** Stable step at fault for an atomic edit plan. */
  stepId?: string;
  /**
   * Le geste suivant, en clair, quand `reinspect` n'en est PAS un (#991).
   *
   * ⚠️ `reinspect` reste le défaut parce qu'il est juste pour les refus où
   * relire change la donne — cible introuvable, révision périmée. Il est FAUX
   * quand la ré-inspection rendra exactement le même document, et c'est ce
   * cas-là qui a fait boucler l'agent six fois sur le pinceau : le message
   * disait quoi faire pendant que le champ machine disait « va relire ». Les
   * deux doivent dire la même chose — d'où `REFUS_A_CORRIGER_SANS_RELIRE`
   * pour les codes dont le message porte déjà tout ce qu'il faut corriger.
   */
  nextAction?: string;
  recovery?: CanvasActionReceipt['recovery'];
  /** Ce qu'un `replace_text` en mode partout a trouvé, quand c'est ce relevé qui fait refuser l'appel. */
  replaceEverywhere?: CanvasReplaceEverywhereReceipt;
}): CanvasActionReceipt {
  return {
    intent: input.intent,
    status: 'aborted',
    reviewability: 'none',
    preSnapshot: input.snapshot,
    postSnapshot: input.snapshot,
    resolvedTargets: [],
    effects: [],
    verification: [],
    verificationPassed: false,
    errors: [{
      code: input.code,
      message: input.message,
      ...(input.stepId === undefined ? {} : { stepId: input.stepId }),
      ...(input.nextAction === undefined ? {} : { nextAction: input.nextAction }),
    }],
    ...(input.replaceEverywhere === undefined ? {} : { replaceEverywhere: input.replaceEverywhere }),
    recovery: input.recovery ?? (REFUS_A_CORRIGER_SANS_RELIRE.has(input.code)
      ? { action: 'retry', reason: 'Refused before any write: nothing was written and the inspected scope is intact. Correct the call as the error message says and resend it, without re-reading.' }
      : { action: 'reinspect', reason: 'The action was refused before mutating the document; nothing was written.' }),
  };
}

/**
 * Une partie de l'action est PROUVÉE, mais le lot demandé n'est pas complet.
 *
 * Ce n'est ni un refus fournisseur pur, ni une réussite dégradée : au moins un
 * effet est déjà présent dans le document, donc une relance le dupliquerait.
 * Le receipt est volontairement plus prudent que les effets individuels : tant
 * que le lot entier n'est pas attribuable, il ne promet aucune relisibilité
 * globale à l'agent ou à l'avocat.
 */
export function partialAfterMutation(input: {
  intent: CanvasActionName;
  preSnapshot: CanvasSnapshotSummary;
  postSnapshot: CanvasSnapshotSummary;
  resolvedTargets: ResolvedActionTarget[];
  code: string;
  message: string;
  /** Stable plan step that could not be proven after a submitted mutation. */
  stepId?: string;
  verification: VerificationCheck[];
  effects: CanvasActionReceipt['effects'];
  formattingMatched?: boolean;
  revisionIdentities?: CanvasActionReceipt['revisionIdentities'];
  recoveryReason: string;
}): CanvasActionReceipt {
  return {
    intent: input.intent,
    status: 'partial',
    // Un lot partiellement attribué n'est ni « suivi » ni « direct » dans son
    // ensemble. Déclarer l'un des deux ferait croire que chaque paragraphe a le
    // même statut alors que c'est précisément ce que la relecture ne prouve pas.
    reviewability: 'none',
    preSnapshot: input.preSnapshot,
    postSnapshot: input.postSnapshot,
    resolvedTargets: input.resolvedTargets,
    effects: input.effects.map((effect) => ({ ...effect, reviewability: 'none' })),
    verification: input.verification,
    verificationPassed: false,
    errors: [{ code: input.code, message: input.message, ...(input.stepId === undefined ? {} : { stepId: input.stepId }) }],
    ...(input.formattingMatched === undefined ? {} : { formattingMatched: input.formattingMatched }),
    ...(input.revisionIdentities === undefined ? {} : { revisionIdentities: input.revisionIdentities }),
    recovery: { action: 'stop', reason: input.recoveryReason },
  };
}

/**
 * Une mutation a été tentée et son effet n'est ni prouvé ni infirmé : document
 * qui a bougé pendant l'action, relecture impossible, accusé de l'éditeur
 * inexploitable. L'appelant dit lequel (`recoveryReason`).
 *
 * Distinct du refus préalable sur révision périmée (`STALE_CANVAS_REVISION`,
 * `canvasAgentRequestGuard`), qui est **préservé** : celui-là refuse *avant*
 * d'agir, celui-ci constate *après*. Les deux existent, et ils ne disent pas la
 * même chose — l'un protège d'une écriture au mauvais endroit, l'autre avoue
 * qu'on ne peut plus rien affirmer.
 */
export function inconclusiveAfterMutation(input: {
  intent: CanvasActionName;
  snapshot: CanvasSnapshotSummary;
  /** Relecture disponible après la mutation, même si elle ne permet pas de l'attribuer. */
  postSnapshot?: CanvasSnapshotSummary;
  resolvedTargets?: ResolvedActionTarget[];
  code: string;
  message: string;
  /** Stable plan step that could not be proven after a submitted mutation. */
  stepId?: string;
  /**
   * Ce que l'appelant a ÉTABLI de la cause. Sans lui, la raison ne dit que ce qui est vrai de tout reçu de ce statut :
   * « le document a changé pendant l'action » était écrit pour tous, y compris quand rien ne l'avait montré
   * (conversation de production du 2026-10-13).
   */
  recoveryReason?: string;
}): CanvasActionReceipt {
  return {
    intent: input.intent,
    status: 'non_verifiable',
    // Rien n'est PROUVÉ relisible : l'effet n'est ni établi ni infirmé, donc
    // annoncer une relisibilité serait affirmer plus que ce qu'on sait.
    reviewability: 'none',
    preSnapshot: input.snapshot,
    postSnapshot: input.postSnapshot ?? input.snapshot,
    resolvedTargets: input.resolvedTargets ?? [],
    effects: [],
    verification: [],
    verificationPassed: false,
    errors: [{ code: input.code, message: input.message, ...(input.stepId === undefined ? {} : { stepId: input.stepId }) }],
    recovery: {
      action: 'stop',
      reason: input.recoveryReason ?? 'A mutation was submitted and the re-read could not prove or disprove its effect; replaying it could apply it twice.',
    },
  };
}

/**
 * Ce que le fournisseur a répondu en refusant, et la suite à donner.
 *
 * Sans le code ni le message du fournisseur, l'agent invente la cause : le
 * 2026-10-08 il a dit à une avocate que Word « verrouillait » des paragraphes,
 * sur un `INVALID_CONTEXT` / `text-range-in-field` que ce reçu taisait.
 *
 * `text-range-in-field` est mesuré sur un champ VIDE (`champsVides.ts`) ; un
 * champ de date complet, lui, se laisse réécrire. D'où une suite propre à ce refus.
 */
function raisonDuFournisseur(provider: unknown, intent: CanvasActionName): string {
  const echec = typeof provider === 'object' && provider !== null ? (provider as { failure?: unknown }).failure : undefined;
  const lu = typeof echec === 'object' && echec !== null ? (echec as { code?: unknown; message?: unknown }) : {};
  const code = typeof lu.code === 'string' ? lu.code : 'no failure code';
  const message = typeof lu.message === 'string' ? lu.message.slice(0, 300) : 'no failure message';
  if (message !== REFUS_TEXTE_DANS_UN_CHAMP) return ` (${code}: ${message}). Re-inspect the affected target before retrying.`;
  return ` (${code}: ${message}): the targeted text spans a Word field that the editor cannot overwrite. `
    + 'Nothing was written. The document is NOT locked: do not tell the lawyer to finish it in Word. Next step: '
    + (intent === 'rewrite_block' || intent === 'replace_text'
      ? `${intent} already removes the empty fields of the block; this one keeps a field that carries content. Edit the text before and after that field with replace_text, each edit on a passage that does not span it.`
      : 'rewrite the whole block with rewrite_block (it removes the empty leftover fields of the block first), or use replace_text on the passage.');
}

function isProviderFailure(result: unknown): boolean {
  return typeof result === 'object' && result !== null && 'success' in result && (result as { success?: unknown }).success === false;
}

/**
 * Ajoute au reçu un constat fait APRÈS l'écriture demandée. Tenu : un contrôle de plus. Non tenu : le reçu `ok`
 * devient `partial` — l'écriture est faite —, tout autre statut garde le sien, et l'erreur dit quoi dire à l'avocat.
 */
export function avecUnConstat(
  recu: CanvasActionReceipt,
  controle: VerificationCheck,
  erreur: CanvasActionReceipt['errors'][number] | undefined,
): CanvasActionReceipt {
  if (erreur === undefined) return { ...recu, verification: [...recu.verification, controle] };
  return {
    ...recu,
    status: recu.status === 'ok' ? 'partial' : recu.status,
    verificationPassed: false,
    verification: [...recu.verification, controle],
    errors: [...recu.errors, erreur],
    recovery: recu.recovery ?? {
      action: 'stop',
      reason: "L'écriture demandée est faite ; le reçu nomme ce qui n'a pas pu être tenu ou vérifié, et rejouer l'appel n'y changerait rien.",
    },
  };
}
